import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { projectService } from '../../../services/projectService';
import { customerService } from '../../../services/customerService';
import { useBottomSheet } from '../../../hooks/useBottomSheet';

const ADU_TYPE_OPTIONS = [
  { value: 'detached', label: 'Detached' },
  { value: 'attached', label: 'Attached' },
  { value: 'garage_conversion', label: 'Garage Conversion' },
  { value: 'basement_conversion', label: 'Basement Conversion' },
  { value: 'junior_adu', label: 'Junior ADU' }
];

const PHASE_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'permits', label: 'Permits' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'framing', label: 'Framing' },
  { value: 'systems', label: 'Systems' },
  { value: 'finishing', label: 'Finishing' },
  { value: 'completed', label: 'Completed' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }
];

const AddProjectModal = ({ isOpen, onClose, onSuccess, preSelectedCustomer }) => {
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    address: '',
    aduType: 'detached',
    phase: 'planning',
    priority: 'medium',
    phaseDuration: '',
    nextMilestone: '',
    budgetTotal: '',
    budgetSpent: '0',
    dueDate: '',
    imageFile: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [error, setError] = useState('');

  const {
    sheetStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    scrollRef,
    showTopShadow,
    showBottomShadow,
    recheckShadows,
  } = useBottomSheet(onClose);

  // Load customers when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCustomers();
    }
  }, [isOpen]);

  // Set pre-selected customer when it changes
  useEffect(() => {
    if (isOpen && preSelectedCustomer) {
      setFormData(prev => ({
        ...prev,
        customerId: preSelectedCustomer?.customerId || '',
        customerName: preSelectedCustomer?.customerName || ''
      }));
    }
  }, [isOpen, preSelectedCustomer]);

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    setError('');
    try {
      console.log('Loading customers...');
      const data = await customerService?.getAll();
      console.log('Customers loaded:', data);
      
      if (Array.isArray(data)) {
        setCustomers(data);
        console.log('Customers set to state:', data?.length, 'customers');
      } else {
        console.error('Invalid data format:', data);
        setCustomers([]);
        setError('Failed to load customers: Invalid data format');
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setCustomers([]);
      setError(`Failed to load customers: ${err?.message || 'Please try again'}`);
    } finally {
      setLoadingCustomers(false);
      recheckShadows();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-fill customer name when customer is selected
    if (name === 'customerId' && value) {
      const selectedCustomer = customers?.find(c => c?.id === value);
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          customerId: value,
          customerName: selectedCustomer?.name,
          address: selectedCustomer?.propertyAddress || ''
        }));
      }
    }
  };

  const handleSelectChange = (name) => (value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (customerId) => {
    if (customerId) {
      const selectedCustomer = customers?.find(c => c?.id === customerId);
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          customerId,
          customerName: selectedCustomer?.name,
          address: selectedCustomer?.propertyAddress || prev?.address
        }));
      } else {
        setFormData(prev => ({ ...prev, customerId }));
      }
    } else {
      setFormData(prev => ({ ...prev, customerId: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      if (!file?.type?.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      // Validate file size (5MB)
      if (file?.size > 5242880) {
        setError('Image size must be less than 5MB');
        return;
      }
      setFormData(prev => ({ ...prev, imageFile: file }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader?.result);
      };
      reader?.readAsDataURL(file);
      setError('');
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, imageFile: null }));
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await projectService?.create({
        ...formData,
        customerId: formData?.customerId || null,
        phaseDuration: formData?.phaseDuration ? parseInt(formData?.phaseDuration) : null,
        budgetTotal: formData?.budgetTotal ? parseFloat(formData?.budgetTotal) : null,
        budgetSpent: formData?.budgetSpent ? parseFloat(formData?.budgetSpent) : 0,
        dueDate: formData?.dueDate || null
      });
      
      onSuccess?.();
      onClose();
      
      // Reset form
      setFormData({
        customerId: '',
        customerName: '',
        address: '',
        aduType: 'detached',
        phase: 'planning',
        priority: 'medium',
        phaseDuration: '',
        nextMilestone: '',
        budgetTotal: '',
        budgetSpent: '0',
        dueDate: '',
        imageFile: null
      });
      setImagePreview(null);
    } catch (err) {
      setError(err?.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const customerOptions = customers?.map(c => ({ value: c?.id, label: c?.name }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <div
        className="bg-card w-full sm:rounded-xl sm:max-w-2xl shadow-2xl rounded-t-2xl max-h-[92vh] flex flex-col"
        style={{
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
          ...(typeof window !== 'undefined' && window.innerWidth < 640 ? sheetStyle : {})
        }}
      >
        {/* Enhanced drag handle on mobile */}
        <div
          className="sm:hidden flex-shrink-0 flex flex-col items-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          <span className="text-[10px] text-muted-foreground/40 mt-1 font-medium tracking-wide">DRAG TO CLOSE</span>
        </div>

        {/* Sticky header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0 bg-card">
          <h2 className="text-lg font-semibold text-foreground">Add New Project</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={22} />
          </button>
        </div>

        {/* Scroll indicator - top */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showTopShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to bottom, hsl(var(--card)) 0%, transparent 100%)', marginBottom: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Scrollable form */}
        <form ref={scrollRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Customer</label>
              <Select
                value={formData?.customerId}
                onChange={handleCustomerChange}
                options={customerOptions}
                placeholder={loadingCustomers ? 'Loading customers...' : 'Select a customer (optional)'}
                disabled={loadingCustomers}
                clearable
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Customer Name <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                name="customerName"
                value={formData?.customerName}
                onChange={handleChange}
                required
                placeholder="Enter customer name"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Address <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                name="address"
                value={formData?.address}
                onChange={handleChange}
                required
                placeholder="1234 Main St, City, State ZIP"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                ADU Type <span className="text-destructive">*</span>
              </label>
              <Select
                value={formData?.aduType}
                onChange={handleSelectChange('aduType')}
                options={ADU_TYPE_OPTIONS}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Phase</label>
              <Select
                value={formData?.phase}
                onChange={handleSelectChange('phase')}
                options={PHASE_OPTIONS}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Priority</label>
              <Select
                value={formData?.priority}
                onChange={handleSelectChange('priority')}
                options={PRIORITY_OPTIONS}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Phase Duration (days)</label>
              <Input
                type="number"
                name="phaseDuration"
                value={formData?.phaseDuration}
                onChange={handleChange}
                placeholder="30"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Due Date</label>
              <Input
                type="date"
                name="dueDate"
                value={formData?.dueDate}
                onChange={handleChange}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1.5">Next Milestone</label>
              <Input
                type="text"
                name="nextMilestone"
                value={formData?.nextMilestone}
                onChange={handleChange}
                placeholder="Site survey completion"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Total Budget</label>
              <Input
                type="number"
                name="budgetTotal"
                value={formData?.budgetTotal}
                onChange={handleChange}
                placeholder="250000"
                min="0"
                step="1000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Budget Spent</label>
              <Input
                type="number"
                name="budgetSpent"
                value={formData?.budgetSpent}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="1000"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1.5">Project Photo</label>
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Project preview"
                    className="w-full h-40 object-cover rounded-lg border border-border"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-destructive text-white p-2 rounded-full hover:bg-destructive/90 transition-colors"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-border rounded-lg p-5 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="projectImage"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label htmlFor="projectImage" className="cursor-pointer flex flex-col items-center gap-2">
                    <Icon name="Upload" size={28} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Tap to upload project photo</span>
                    <span className="text-xs text-muted-foreground">PNG, JPG, WEBP up to 5MB</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Scroll indicator - bottom */}
        <div
          className={`flex-shrink-0 h-5 pointer-events-none transition-opacity duration-200 ${showBottomShadow ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to top, hsl(var(--card)) 0%, transparent 100%)', marginTop: '-20px', position: 'relative', zIndex: 2 }}
        />

        {/* Sticky footer */}
        <div className="flex gap-3 px-5 pt-3 border-t border-border flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1 h-12">
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="flex-1 h-12" onClick={handleSubmit}>
            {loading ? 'Creating...' : 'Create Project'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddProjectModal;